import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LogOut, Truck, Loader2 } from "lucide-react";

import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { pinLogout } from "../../store/slices/authSlice";
import {
  getPairedTruck,
  hasDeviceSession,
  DEVICE_ROUTE,
} from "../../utils/deviceSession";
import { vehicleLabel } from "../../utils/vehicleLabel";

/**
 * The bar at the top of every screen a driver sees on a truck's device.
 *
 * It answers the two questions a shared screen has to answer continuously, and
 * that a personal phone never has to answer at all:
 *
 *   which truck am I looking at   the plate, because the work below belongs to
 *                                 the truck and not to the person, and a driver
 *                                 who has climbed into the wrong cab must find
 *                                 out here rather than at a terminal gate
 *   who am I signed in as         because the next step they record will be
 *                                 stamped with that name
 *
 * And it carries the one control the shared screen needs: End shift. Without it
 * a handover means the next driver either uses the previous one's session — with
 * every step recorded under the wrong name — or waits for the idle timeout.
 *
 * Renders nothing unless the *current session* is a truck session.
 *
 * Deliberately not "unless this tablet is paired". A driver can sign in with
 * their email and password on the truck's own screen, and for that session there
 * is no shift to end and no truck to name — showing a plate would claim a
 * connection to work the server refuses them, and "End shift" would be a second,
 * subtly different logout sitting next to the real one.
 */
export function TruckHeader() {
  const { t } = useTranslation("devices");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  const [busy, setBusy] = useState(false);
  const truck = getPairedTruck();

  if (!truck || !hasDeviceSession()) return null;

  const endShift = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // The thunk clears the local session whether or not the call reaches the
      // server, so there is no failure branch here worth a different landing:
      // a driver who tapped End shift has ended it.
      await dispatch(pinLogout());
    } finally {
      navigate(DEVICE_ROUTE, { replace: true });
    }
  };

  return (
    <div className="flex items-center gap-3 border-b border-[#ECEAE4] bg-white px-4 py-2.5">
      <Truck className="h-5 w-5 shrink-0 text-[#2F7A5B]" />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold tracking-wide text-[#0B0F0E]">
          {vehicleLabel(truck)}
        </p>
        {user && (
          <p className="truncate text-xs text-[#8A9590]">
            {user.firstName} {user.lastName}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={endShift}
        disabled={busy}
        className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[#ECEAE4] px-3 py-1.5 text-sm font-medium text-[#5A6460] transition-colors hover:bg-[#F5F4F0] disabled:opacity-50"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <LogOut className="h-4 w-4" />
        )}
        {t("shift.end")}
      </button>
    </div>
  );
}
